import { bindActionCreators, combineReducers, createStore } from 'redux';
import reducers from './reducers';
import { useDispatch } from 'react-redux';
import { useMemo } from 'react';
import {
  forEach,
  isArray,
  isEmpty,
  isObject,
  isString,
  keys,
  map,
  reduce,
  values,
  get,
  set,
  cloneDeep,
  includes,
  first,
  toUpper,
  has,
  isNil,
  find
} from 'lodash';
import { ReduxStore } from './payload-types';
import {
  SUPPORTED_LOCALES,
  MANULIFE_LOGO_LABEL,
  MANULIFE_LOGO_LABEL_FRENCH,
  ORG_CATEGORY_AGENCY,
  COVERME_REFERENCE_ID,
  ORG_CATEGORY_BROKER
} from '@trip-ux/constants';
import { Organization, STORE } from './global';
import { ProfileResponse, UserListingResponse } from './user-listing';
import { UserInfoRequest } from './user-info';
import { BREAKDOWN_TYPES, BREAKDOWN_TYPES_API, ME_EMPLOYEE_TYPES, ME_EMPLOYEE_TYPES_API } from './quote-form';
import { Adjustment, Discounts } from './quote';
import { dayjs } from '@trip-ux/common';

export const useActions = actions => {
  const dispatch = useDispatch();
  return useMemo(() => {
    return bindActionCreators(actions, dispatch);
  }, [dispatch]);
};

export const backEndProcessing = (
  initialState: Record<string, unknown>,
  promises: Record<string, Promise<unknown>>
): Promise<ReduxStore> => {
  const backendStore = createStore(combineReducers(reducers), initialState);
  return Promise.all(values(promises))
    .then(resolveValues =>
      forEach(keys(promises), (key, idx) =>
        backendStore.dispatch({
          type: key,
          payload: resolveValues[idx]
        })
      )
    )
    .then(() => backendStore.getState() as ReduxStore);
};

const traverseArray = (array, regex, contextPath, suffix) =>
  map(array, val => {
    if (isArray(val)) {
      return traverseArray(val, regex, contextPath, suffix);
    } else if (isObject(val)) {
      return traverseAndUpdate(val, regex, contextPath, suffix);
    } else {
      return isString(val) && val.match(regex) && !isEmpty(contextPath) ? `${contextPath}${val}${suffix}` : val;
    }
  });

export const traverseAndUpdate = (obj, regex, contextPath, suffix) =>
  isArray(obj)
    ? traverseArray(obj, regex, contextPath, suffix)
    : reduce(
        obj,
        (res, val, key) => {
          if (isArray(val)) {
            res[key] = traverseArray(val, regex, contextPath, suffix);
          } else if (isObject(val)) {
            res[key] = traverseAndUpdate(val, regex, contextPath, suffix);
          } else {
            res[key] =
              isString(val) && val.match(regex) && !isEmpty(contextPath) ? `${contextPath}${val}${suffix}` : val;
          }
          return res;
        },
        {}
      );

export const mapOrganizationNameContent = (data, lang) => {
  const { organizations } = data;
  const organization = cloneDeep(organizations[0]);

  let organizationName = '';
  let manulifeLabel = '';
  if (lang === SUPPORTED_LOCALES.FRENCH) {
    organizationName = get(organization, 'organizationNameInFrench');
    manulifeLabel = MANULIFE_LOGO_LABEL_FRENCH;
  } else {
    organizationName = get(organization, 'organizationName');
    manulifeLabel = MANULIFE_LOGO_LABEL;
  }

  set(organization, ['contract', 'content', 'banner', 'whiteLabelBranding', 'label'], organizationName);
  set(organization, ['contract', 'content', 'banner', 'manulifeBranding', 'label'], manulifeLabel);
  set(
    organization,
    ['contract', 'content', 'sideNav', 'branding', 'label', 'needAssistance', 'eligibilityCannotBeDeterminedUrl'],
    organizationName
  );
  return { ...data, organization };
};

export const getOrganizationCode = data => {
  return get(data, 'contentFragment.organizationCode');
};

// Re-export from reducer-utils to maintain backwards compatibility
// These are defined in reducer-utils.ts to break circular dependencies
export { checkWhitelist } from './reducer-utils';

const mapProfile = (profile: ProfileResponse) => ({
  profileId: get(profile, 'id'),
  organizationCode: get(profile, 'organizationCode'),
  agentId: get(profile, 'agentId'),
  firstName: get(profile, 'user.firstName'),
  lastName: get(profile, 'user.lastName'),
  emailAddress: get(profile, 'user.email'),
  role: get(profile, 'user.role'),
  preferredLang: get(profile, 'user.language'),
  provinceLicense: get(profile, 'user.province'),
  userRef: get(profile, 'user.userRef'),
  status: get(profile, 'status'),
  agencyCode: get(profile, 'agencyCode'),
  sellerCode: get(profile, 'sellerCode')
});

const mapAgentProfile = (profile: ProfileResponse) => ({
  organizationCode: get(profile, 'organizationCode'),
  agentId: get(profile, 'agentId'),
  firstName: get(profile, 'firstName'),
  lastName: get(profile, 'lastName'),
  emailAddress: get(profile, 'email'),
  role: get(profile, 'role'),
  preferredLang: get(profile, 'language'),
  provinceLicense: get(profile, 'province'),
  userRef: '',
  status: get(profile, 'status'),
  agencyCode: get(profile, 'agencyCode'),
  profileCode: get(profile, 'profileCode'),
  profileRef: get(profile, 'profileRef'),
  employmentType: get(profile, 'employmentType'),
  sellingCode: get(profile, 'organizations[0].weblinks[0].sellingCode'),
  partyId: get(profile, 'organizations[0].partyId')
});

export const mapProfileToAgentListing = (profiles: ProfileResponse[]): UserListingResponse => {
  return {
    records: map(profiles, mapAgentProfile)
  };
};

export const mapProfileToUserListing = (profiles: ProfileResponse[]): UserListingResponse => {
  return {
    records: map(profiles, mapProfile)
  };
};

export const mapProfileToUserInfo = (profiles: ProfileResponse[]): UserInfoRequest => {
  return first(map(profiles, mapProfile));
};

export const additionalInfo = travellerDetail => {
  const { schoolName, schoolCity, relationship, language, homeCountry } = travellerDetail ?? {};
  return {
    ...(!isEmpty(schoolName) && { schoolName }),
    ...(!isEmpty(schoolCity) && { schoolCity }),
    ...(!isEmpty(relationship) && { relationship: toUpper(relationship) }),
    ...(!isEmpty(language) && { lang: toUpper(language) }),
    ...(!isEmpty(homeCountry) && { countryOfResidence: homeCountry })
  };
};

export const getManulifeEmployeeRelationShip = (employeeType: string): string => {
  switch (employeeType) {
    case ME_EMPLOYEE_TYPES.MANULIFE_EMPLOYEE:
      return ME_EMPLOYEE_TYPES_API.MANULIFE_EMPLOYEE;
    case ME_EMPLOYEE_TYPES.RESIDE_WITH_MANULIFE_EMPLOYEE:
      return ME_EMPLOYEE_TYPES_API.RESIDE_WITH_MANULIFE_EMPLOYEE;
    case ME_EMPLOYEE_TYPES.NOT_RESIDE_WITH_MANULIFE_EMPLOYEE:
      return ME_EMPLOYEE_TYPES_API.NOT_RESIDE_WITH_MANULIFE_EMPLOYEE;
    default:
      return undefined;
  }
};

export const getDiscounts = (adjustments: Adjustment[]): Discounts => {
  let manulifeEmployeeRelationship;
  let hasCompanionDiscount = false;
  forEach(adjustments, adjustment => {
    const breakdownType = get(adjustment, 'breakdownType');
    switch (breakdownType) {
      case BREAKDOWN_TYPES.COMPANION:
        hasCompanionDiscount = true;
        break;
      case BREAKDOWN_TYPES.EMPLOYEE:
        manulifeEmployeeRelationship = BREAKDOWN_TYPES_API.EMPLOYEE;
        break;
      case BREAKDOWN_TYPES.FAMILYFRIENDSRESIDING:
        manulifeEmployeeRelationship = BREAKDOWN_TYPES_API.FAMILYFRIENDSRESIDING;
        break;
      case BREAKDOWN_TYPES.FAMILYFRIENDSNOTRESIDING:
        manulifeEmployeeRelationship = BREAKDOWN_TYPES_API.FAMILYFRIENDSNOTRESIDING;
        break;
    }
  });

  return {
    manulifeEmployeeRelationship,
    hasCompanionDiscount
  };
};

// Re-export from reducer-utils to maintain backwards compatibility
export { formatDateFields, isToTranslateDate } from './reducer-utils';

export const isAgency = (organizationCategory: string): boolean => {
  return organizationCategory === ORG_CATEGORY_AGENCY;
};

export const isBroker = (organizationCategory: string): boolean => {
  return organizationCategory === ORG_CATEGORY_BROKER;
};

export const isReferenceId = (referenceId: string): boolean => {
  return referenceId === COVERME_REFERENCE_ID;
};

export const checkFeatureFlag = (featureFlags, flagName) => {
  return featureFlags?.some(flag => flag.name === flagName && flag.value === true);
};

// Re-exported from api-utils to maintain backwards compatibility
export { getRefCode } from './api-utils';

export const sortArray = (array: any[], locale: string): any[] => {
  const localeKey = locale === 'fr' ? 'organizationNameInFrench' : 'organizationName';
  return array.sort((a, b) => a[localeKey].localeCompare(b[localeKey], locale, { sensitivity: 'base' }));
};

export const sortObject = (obj, locale) => {
  const localeKey = locale === 'fr' ? 'organizationNameInFrench' : 'organizationName';

  // Convert object to array of key-value pairs
  const entries = Object.entries(obj);

  // Sort the array based on the locale key
  const comparator = ([, a], [, b]) => a[localeKey].localeCompare(b[localeKey], locale, { sensitivity: 'base' });

  entries.sort(comparator);
  const sortedEntries = entries;
  return sortedEntries.map(([_, value]) => value);
};
